import { DashboardStore } from "./dashboard.store";
import { HttpClient } from "@angular/common/http";

import { LoginService } from "./../login/login.service";
import { Intercom } from "@supy-io/ngx-intercom";
import {
  Injectable,
  AfterViewInit,
  signal,
  WritableSignal,
  computed,
  Signal,
  effect,
} from "@angular/core";
import { Router } from "@angular/router";

import { LocalStorageService } from "src/app/utils/localstorage.service";
import { environment } from "../../environments/environment";
import { Observable, Subject } from "rxjs";
import { ConversationsService } from "./conversations/conversations.service";
import * as _ from "underscore";
import { AccountantService } from "./main/accountant/accountant.service";
import { SharedDataService } from "./shared-data.service";
import { AppTitleService } from "./app-title.service";

@Injectable({ providedIn: "root" })
export class DashboardService {
  show_loading = new Subject<any>();

  clientsLoaded = new Subject<any>();
  conversationsLoaded = new Subject<any>();

  dispatchNewMessage = new Subject<any>();
  dispatchNewSocketMessage = new Subject<any>();
  newMessage = new Subject<any>();
  getOpenTrackedItemsCount = new Subject<any>();

  openDeleteTrackedItemModal = new Subject<any>();
  closeJobDrawer$ = new Subject<any>();

  // New subects for badges
  updateTrackedItems = new Subject();
  updateReadStatus = new Subject();

  unread_conversations: number = 0;
  opened_tracked_items: number = 0;
  total_tracked_items: number = 0;

  paymentDone = new Subject();
  startTimeTracking = new Subject();

  public clients = [];
  public conversations = [];

  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private conversationsService: ConversationsService,
    private intercom: Intercom,
    private accountantService: AccountantService,
    private sharedDataService: SharedDataService,
    public dashboardStore: DashboardStore,
    private appTitleService: AppTitleService,
  ) {
    console.log("DashboardService loaded");

    this.dashboardStore.clientsLoaded.subscribe((res: any) => {
      this.show_loading.next(false);
      this.conversationsService.updateOpenTrackedItemsBadge(
        this.dashboardStore.clients,
      );
      this.updateOpenTrackedItemsBadge();

      const user = this.localStorage.get("user");
      const data = this.localStorage.get("data");
      const company = this.sharedDataService.company;
      if (!user || !data || !company) return;

      this.intercom.update({
        app_id: "wqv1cgow",
        name: `${user["first_name"]} ${user["last_name"]}`,
        email: user["email"],
        userid: data["uuid"],
        intercom_user_jwt: user["intercom_user_jwt"],
        company: {
          id: company["uuid"],
          company_id: company["uuid"],
          name: company["name"],
        },
        custom_data: {
          client_companies: this.dashboardStore.clients.length,
        },
      });
    });

    // Triggered when new client is added
    this.accountantService.clients.subscribe((sub) => {
      if (sub.company != undefined) {
        this.dashboardStore.clients.push(sub.company);

        // Fixes bug that when a new client is added
        // the conversation subscription was not being made
        this.dashboardStore.clientsLoaded.next(null);
      }
    });
    effect(() => {
      console.log("new value", this.filters());
      this.localStorage.set("dashboardsFilters", this.filters());
    });
  }
  filters: WritableSignal<any> = signal(
    this.localStorage.get("dashboardsFilters") || {},
  );

  captureFilters(
    dashboardName: string,
    extraKeys: string[] = [],
  ): Record<string, unknown> {
    const dashboardFilters = this.filters()[dashboardName] || {};
    const extra: Record<string, unknown> = {};

    extraKeys.forEach((key) => {
      extra[key] = this.localStorage.get(key);
    });

    return {
      ...dashboardFilters,
      __extra: extra,
    };
  }

  applyFilters(
    dashboardName: string,
    snapshot: Record<string, unknown>,
    extraKeys: string[] = [],
  ): void {
    const { __extra, ...dashboardFilters } = snapshot;
    const extraValues =
      (__extra as Record<string, unknown> | undefined) || {};

    this.filters.update((prevState) => ({
      ...prevState,
      [dashboardName]: {
        ...(dashboardFilters as Record<string, unknown>),
      },
    }));

    extraKeys.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(extraValues, key)) {
        this.localStorage.set(key, extraValues[key]);
      }
    });
  }

  hasActiveFilters(
    dashboardName: string,
    initialFilters: Record<string, unknown>,
    extraKeys: string[] = [],
  ): boolean {
    const defaults = initialFilters[dashboardName] as
      | Record<string, unknown>
      | undefined;
    if (!defaults) {
      return false;
    }

    const current = (this.filters()[dashboardName] || {}) as Record<
      string,
      unknown
    >;
    if (
      !this.filtersEqual(
        this.stripNonFilterKeys(current, defaults),
        this.stripNonFilterKeys(defaults, defaults),
      )
    ) {
      return true;
    }

    return extraKeys.some((key) => {
      const currentExtra = this.localStorage.get(key);
      const defaultExtra = this.getDefaultExtraValue(key);
      return !this.filtersEqual(currentExtra, defaultExtra);
    });
  }

  clearFilters(
    dashboardName: string,
    initialFilters: Record<string, unknown>,
    extraKeys: string[] = [],
  ): void {
    const defaults = initialFilters[dashboardName];
    if (!defaults) {
      return;
    }

    this.filters.update((prevState) => ({
      ...prevState,
      [dashboardName]: { ...(defaults as Record<string, unknown>) },
    }));

    extraKeys.forEach((key) => {
      this.localStorage.set(key, this.getDefaultExtraValue(key));
    });
  }

  getSelectedFilterPreset(
    dashboardName: string,
  ): { uuid: string; snapshot: string } | null {
    const selections =
      this.localStorage.get("dashboardFilterPresetSelections") || {};
    const value = selections[dashboardName];

    if (!value) {
      return null;
    }

    if (typeof value === "string") {
      return { uuid: value, snapshot: "" };
    }

    return value as { uuid: string; snapshot: string };
  }

  setSelectedFilterPreset(
    dashboardName: string,
    selection: { uuid: string; snapshot: string } | null,
  ): void {
    const selections = {
      ...(this.localStorage.get("dashboardFilterPresetSelections") || {}),
    };

    if (selection) {
      selections[dashboardName] = selection;
    } else {
      delete selections[dashboardName];
    }

    this.localStorage.set("dashboardFilterPresetSelections", selections);
  }

  filterSnapshotsEqual(a: unknown, b: unknown): boolean {
    return this.filtersEqual(a, b);
  }

  private getDefaultExtraValue(key: string): unknown {
    if (key.includes("tags_filter")) {
      return [];
    }
    if (key.includes("and_or")) {
      return "or";
    }
    if (key === "selectedDisplayType") {
      return "column";
    }
    return null;
  }

  private filtersEqual(a: unknown, b: unknown): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  private stripNonFilterKeys(
    filters: Record<string, unknown>,
    defaults: Record<string, unknown>,
  ): Record<string, unknown> {
    const result = { ...filters };

    delete result.rows;

    if (Array.isArray(defaults.columns) && defaults.columns.length === 0) {
      delete result.columns;
    }

    return result;
  }
  // activeDashboard: WritableSignal<string> = signal("");
  // filters = computed(() => {
  //   const defaultFilters = this.localStorage.get("dashboardsFilters");
  //   if (defaultFilters) return defaultFilters[this.activeDashboard()];
  //   return null;
  // });

  // setDefaultFilters(filters) {
  //   this.localStorage.set("dashboardFilters", {
  //     ...this.filters(),
  //     [this.activeDashboard()]: filters,
  //   });
  //   console.log(
  //     `Default filters set for dashboard ${this.activeDashboard()}. Values: ${filters} `,
  //   );
  // }

  /**
   * Aggregated dashboard-shell payload (permissions, feature flags,
   * subscription type, counters, etc.) fetched in a single request.
   */
  getBootstrap(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/bootstrap`).toPromise();
  }

  timeTrackingStatus() {
    const url = `${environment.apiUrl}/jobs/time-tracking-status`;
    return this.http.get<any>(url).toPromise();
  }

  get_apps(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/apps/`).toPromise();
  }

  get_resources(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/resources/`).toPromise();
  }

  get_client_apps(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/apps/`).toPromise();
  }

  get_open_tracked_items_count(): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/conversations/tracked-items.count`)
      .toPromise();
  }

  updateUnreadMessagesCount(): void {
    this.appTitleService.refresh();
  }

  updateOpenTrackedItemsBadge(): void {
    let opens = 0;
    this.conversations.forEach((c) => {
      opens =
        opens +
        c.tracked_items.filter((t) => {
          return t.is_open;
        }).length;
    });

    this.opened_tracked_items = opens;
  }

  isHuddleAvailable() {
    return Promise.resolve(false);
    // return this.http.get(`${environment.apiUrl}/company/huddle`).toPromise();
  }

  isBooksReviewAvailable() {
    return this.http
      .get(`${environment.apiUrl}/company/books-review`)
      .toPromise();
  }
}
